import { Alert, Button, Card, Form, Input, InputNumber, Select, Space, Table, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from './components/EducationPageChrome';
import { EducationGradeSelect, EducationSubjectSelect } from './components/EducationLookupSelects';

type Scheme = { id: string; name: string; grade_name: string; subject_name: string };
type Line = {
  id: string; component_code?: string | null; topic?: string | null;
  target_count: number; marks_hint?: number | null; sequence_no: number;
};
type Blueprint = { id: string; name: string; scheme_name?: string; subject_name?: string; grade_name?: string; lines: Line[] };

export default function QuestionBlueprintDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const permissions = useAuthStore((s) => s.user?.permissions || []);
  const canManage = permissions.includes('education.question.manage') || permissions.includes('education.admin');
  const schemes = useQuery({
    queryKey: ['education-assessment-schemes-for-blueprint'],
    enabled: isNew,
    queryFn: async () => (await api.get<Scheme[]>('/education/assessments/schemes')).data || [],
  });
  const blueprint = useQuery({
    queryKey: ['education-question-blueprint', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<Blueprint>(`/education/question-blueprints/${id}`)).data,
  });
  const create = useMutation({
    mutationFn: (values: Record<string, unknown>) => api.post<{ id: string }>('/education/question-blueprints', values),
    onSuccess: (result) => {
      message.success(t('saved'));
      navigate(`/education/question-blueprints/${result.data?.id}`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });
  const addLine = useMutation({
    mutationFn: (values: Record<string, unknown>) => api.post(`/education/question-blueprints/${id}/lines`, values),
    onSuccess: () => {
      message.success(t('saved'));
      queryClient.invalidateQueries({ queryKey: ['education-question-blueprint', id] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });
  return (
    <EducationPageChrome
      title={t(isNew ? 'newQuestionBlueprint' : 'questionBlueprint')}
      extra={<Button onClick={() => navigate('/education/question-blueprints')}>{t('back')}</Button>}
    >
      <Alert type="info" showIcon message={t('blueprintGuardrail')} style={{ marginBottom: 16 }} />
      {isNew ? (
        <Card size="small" style={{ maxWidth: 800 }}>
          <Form layout="vertical" onFinish={(values) => create.mutate(values)}>
            <Form.Item name="name" label={t('name')} rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="assessment_scheme_id" label={t('assessmentScheme')}>
              <Select allowClear options={(schemes.data || []).map((scheme) => ({
                value: scheme.id, label: `${scheme.name} — ${scheme.grade_name} / ${scheme.subject_name}`,
              }))} />
            </Form.Item>
            <Alert type="warning" showIcon message={t('blueprintScopeAlternative')} style={{ marginBottom: 16 }} />
            <Form.Item name="subject_id" label={t('subject')}><EducationSubjectSelect /></Form.Item>
            <Form.Item name="grade_level_id" label={t('grade')}><EducationGradeSelect /></Form.Item>
            <Button type="primary" htmlType="submit" loading={create.isPending}>{t('save')}</Button>
          </Form>
        </Card>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Card size="small" title={blueprint.data?.name} loading={blueprint.isLoading}>
            <Table rowKey="id" pagination={false} dataSource={blueprint.data?.lines || []} columns={[
              { title: t('componentCode'), dataIndex: 'component_code', render: (value) => value || '—' },
              { title: t('topic'), dataIndex: 'topic', render: (value) => value || '—' },
              { title: t('targetCount'), dataIndex: 'target_count' },
              { title: t('marksHint'), dataIndex: 'marks_hint', render: (value) => value ?? '—' },
            ]} />
          </Card>
          {canManage && <Card size="small" title={t('addBlueprintLine')}>
            <Form layout="inline" onFinish={(values) => addLine.mutate(values)}>
              <Form.Item name="component_code"><Input placeholder={t('componentCode')} /></Form.Item>
              <Form.Item name="topic"><Input placeholder={t('topic')} /></Form.Item>
              <Form.Item name="target_count" rules={[{ required: true }]}><InputNumber min={1} placeholder={t('targetCount')} /></Form.Item>
              <Form.Item name="marks_hint"><InputNumber min={0} placeholder={t('marksHint')} /></Form.Item>
              <Button type="primary" htmlType="submit" loading={addLine.isPending}>{t('add')}</Button>
            </Form>
          </Card>}
        </Space>
      )}
    </EducationPageChrome>
  );
}
